import { Alert, Button, Stack, Step, StepLabel, Stepper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Form, Formik, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trackEvent, TrackingEventTypes } from '../../common/tracking';
import { createPatient, InitInfo, resetPatientEdited } from '../../store/patientsSlice';
import { AppDispatch, RootState } from '../../store/store';
import { PageBox } from '../ui/DaliaComponents';
import PatientInitStepConfirmation from './PatientInitStepConfirmation';
import PatientInitStepDoctorSelect from './PatientInitStepDoctorSelect';
import PatientInitStepExclusion from './PatientInitStepExclusion';
import PatientInitStepInclusion from './PatientInitStepInclusion';
import PatientInitStepPatientInfo from './PatientInitStepPatientInfo';

const PatientInitWizard = () => {
  const dispatch: AppDispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const { patientEdited, error } = useSelector((state: RootState) => state.patients);
  const { profile } = useSelector((state: RootState) => state.auth);

  // Define steps based on user role
  const isArc = (profile?.role ?? '').toUpperCase() === 'ARC';
  // Create steps array
  const getSteps = () => {
    // Base steps for all users
    const baseSteps = [PatientInitStepInclusion, PatientInitStepExclusion, PatientInitStepPatientInfo];

    // For ARC users, add doctor selection step
    if (isArc) {
      return [...baseSteps, PatientInitStepDoctorSelect, PatientInitStepConfirmation];
    }

    // For DOCTOR users, just add final step
    return [...baseSteps, PatientInitStepConfirmation];
  };

  const steps = getSteps();
  const ActiveStep = steps[currentStep];
  const validationSchema = ActiveStep.validationSchema(profile);

  const navigate = useNavigate();

  useEffect(() => {
    trackEvent(TrackingEventTypes.ADD_PATIENT_STARTED);
    dispatch(resetPatientEdited());
  }, [dispatch]);

  const gotBackHome = () => {
    if (currentStep === 3) {
      // user clicks on "Remplir plus tard"
      trackEvent(TrackingEventTypes.ADD_PATIENT_NEXT_STEP, { 'Go to questionnaire': false });
    }
    navigate('/home');
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const onSubmit = async (values: Partial<InitInfo>) => {
    const isLastStep = currentStep === steps.length - 1;
    const isSecondToLastStep = currentStep === steps.length - 2;

    if (isSecondToLastStep) {
      // Handle patient creation at the second to last step
      await handleCreatePatient(values);
    } else if (isLastStep) {
      // Last step (confirmation screen)
      if (patientEdited) {
        trackEvent(TrackingEventTypes.ADD_PATIENT_NEXT_STEP, { 'Go to questionnaire': true });
        navigate(`/patient/${patientEdited.id}/complete`);
      }
      return;
    } else {
      // Any other step, just go to the next step
      nextStep();
    }
  };

  const handleCreatePatient = async (values: Partial<InitInfo>) => {
    // If user is a DOCTOR, they create for themselves
    if (profile?.role === 'DOCTOR') {
      values.doctorId = profile.id || '';
    }
    // Otherwise (ARC), they must have selected a doctor in the wizard step
    try {
      await dispatch(createPatient(values)).unwrap();
      nextStep();
    } catch (error) {
      console.error('Failed to create patient:', error);
    }
  };

  // Calculate initialValues from all steps
  const initialValues: Partial<InitInfo> = steps.reduce(
    (values, { initialValues }) => ({
      ...values,
      ...initialValues,
    }),
    {},
  );

  const NavigationButtons = () => {
    const { validateForm } = useFormikContext();

    const handlePrevStep = () => {
      prevStep();
      setTimeout(() => {
        void validateForm();
      }, 0);
    };

    return (
      <Grid container sx={{ mt: 4 }}>
        <Grid size={4}>
          {currentStep > 0 && currentStep < 3 && (
            <Button color="primary" onClick={handlePrevStep} variant="outlined">
              Précédent
            </Button>
          )}
        </Grid>
        <Grid size={8}>
          <Grid container justifyContent="flex-end">
            <Button onClick={gotBackHome} variant="text">
              {currentStep < steps.length - 1 && <div>Annuler</div>}
              {currentStep === steps.length - 1 && <div>Remplir plus tard</div>}
            </Button>
            <Button color="primary" type="submit" variant="contained">
              {currentStep < steps.length - 2 && <div>Suivant</div>}
              {currentStep === steps.length - 2 && <div>Créer le patient</div>}
              {currentStep === steps.length - 1 && <div>Ok, je passe au questionnaire</div>}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <PageBox title="Inclusion d'un nouveau patient">
      {error && (
        <Stack direction="column">
          <Alert severity="error">{error}</Alert>
          <Button onClick={gotBackHome} variant="text">
            Annuler
          </Button>
        </Stack>
      )}
      {!error && (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
          {(formik) => (
            <Form noValidate>
              <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 5 }}>
                {steps.map((_, index) => {
                  return (
                    <Step completed={currentStep > index} key={index}>
                      <StepLabel>{steps[index].label}</StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
              <ActiveStep formik={formik}></ActiveStep>
              <NavigationButtons />
            </Form>
          )}
        </Formik>
      )}
    </PageBox>
  );
};

export default PatientInitWizard;
